import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronsUpDown, Link2, LogOut, Plus, Sparkles } from 'lucide-react';
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader,
  SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarProvider, SidebarTrigger, useSidebar,
} from '@/components/ui/sidebar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Logo } from './Logo';
import { useFeeds, useMe, keys } from '@/lib/queries';
import { post } from '@/lib/api';
import { CreateFeedDialog, openCreateFeed } from '@/features/feeds/CreateFeedDialog';
import { EventDialog } from '@/features/events/EventDialog';
import { OverrideDialog } from '@/features/events/OverrideDialog';
import { ImportDialog } from '@/features/events/ImportDialog';
import { DeletedDialog } from '@/features/events/DeletedDialog';

function NavLinks() {
  const { pathname } = useLocation();
  const feeds = useFeeds();
  const { setOpenMobile } = useSidebar();
  const close = () => setOpenMobile(false);
  const active = /^\/feeds\/([^/]+)/.exec(pathname)?.[1];
  return (
    <>
      <SidebarGroup>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={pathname === '/'}>
              <Link to="/" onClick={close}>
                <CalendarDays />
                <span>All calendars</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
      <SidebarGroup>
        <SidebarGroupLabel>Calendars</SidebarGroupLabel>
        <SidebarGroupAction title="New calendar" onClick={() => openCreateFeed('manual')}>
          <Plus />
          <span className="sr-only">New calendar</span>
        </SidebarGroupAction>
        <SidebarGroupContent>
          <SidebarMenu>
            {feeds.isPending
              ? [1, 2, 3].map((i) => (
                  <SidebarMenuItem key={i}>
                    <SidebarMenuSkeleton />
                  </SidebarMenuItem>
                ))
              : feeds.data?.length
                ? feeds.data.map((f) => (
                    <SidebarMenuItem key={f.id}>
                      <SidebarMenuButton asChild isActive={f.id === active} tooltip={f.name}>
                        <Link to={`/feeds/${f.id}`} onClick={close}>
                          <span className="size-2.5 shrink-0 rounded-[3px]" style={{ background: f.color }} />
                          <span>{f.name}</span>
                          {f.kind === 'external' ? <Link2 className="text-muted-foreground ml-auto size-3!" /> : null}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))
                : <p className="text-muted-foreground px-2 py-1 text-xs">No calendars yet</p>}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
}

function UserMenu() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const u = me.data!;
  const initial = (u.name || u.email)[0].toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="lg" aria-label="Account menu" className="data-[state=open]:bg-sidebar-accent">
          <Avatar className="size-8 rounded-lg">
            <AvatarFallback className="bg-brand-soft text-primary rounded-lg font-semibold">{initial}</AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium">{u.name || u.email}</span>
            <span className="text-muted-foreground truncate text-xs">{u.email}</span>
          </div>
          <ChevronsUpDown className="ml-auto size-4" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-(--radix-dropdown-menu-trigger-width) min-w-56">
        <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">{u.email}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => navigate('/ai')}>
          <Sparkles /> AI &amp; API
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={async () => {
            await post('/auth/logout');
            qc.clear();
            qc.setQueryData(keys.me, null);
            navigate('/');
          }}
        >
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader>
          <Link to="/" className="flex items-center gap-2.5 px-2 py-1.5 font-semibold tracking-tight">
            <Logo />
            Calendar Feed
          </Link>
        </SidebarHeader>
        <SidebarContent>
          <NavLinks />
        </SidebarContent>
        <SidebarFooter className="nav-bottom">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => openCreateFeed('external')}>
                <Link2 />
                <span>Connect a calendar</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={pathname === '/ai'}>
                <Link to="/ai">
                  <Sparkles />
                  <span>AI &amp; API</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <UserMenu />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <header className="bg-background/90 sticky top-0 z-20 flex h-12 items-center gap-2 border-b px-3 backdrop-blur md:hidden">
          <SidebarTrigger />
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
            <Logo className="size-6" /> Calendar Feed
          </Link>
        </header>
        <main className="w-full min-w-0 flex-1 px-4 py-5 md:px-8 md:py-7">{children}</main>
      </SidebarInset>
      <CreateFeedDialog />
      <EventDialog />
      <OverrideDialog />
      <ImportDialog />
      <DeletedDialog />
    </SidebarProvider>
  );
}
